import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { MetricDataPoint, MetricRangeDataPoint } from '../../gql/generated';
import { BOOLEAN_SIGNAL_THRESHOLD, toBinarySignalState } from '../../utils/booleanSignals';

interface MachineBarChartProps {
  /** Data points - can be single machine (MetricDataPoint[]) or multi-machine (MetricRangeDataPoint[]) */
  dataPoints: MetricDataPoint[] | MetricRangeDataPoint[];
  /** Unit of measurement (e.g., '°', 'bar') */
  unit: string;
  /** Alarm thresholds to display as reference lines */
  alarmThresholds?: {
    minimum?: number;
    maximum?: number;
  };
  /** Render chart in boolean alarm mode (off/on axis). */
  isBooleanSignal?: boolean;
}

/**
 * MachineBarChart - Displays metric data as bar charts with min/max ranges
 *
 * Shows min/max range data as stacked bars.
 * This captures spikes (min/max) within each time resolution.
 * Works for both single machine and multi-machine aggregated data.
 */
export function MachineBarChart({
  dataPoints,
  unit,
  alarmThresholds,
  isBooleanSignal = false,
}: MachineBarChartProps) {
  const normalizedDataPoints = isBooleanSignal
    ? dataPoints.map((point) => ({
        ...point,
        min: toBinarySignalState(point.min),
        max: toBinarySignalState(point.max),
        average: toBinarySignalState(point.average),
      }))
    : dataPoints;

  const normalizedThresholds = isBooleanSignal
    ? {
        minimum:
          alarmThresholds?.minimum !== undefined
            ? BOOLEAN_SIGNAL_THRESHOLD
            : alarmThresholds?.minimum,
        maximum:
          alarmThresholds?.maximum !== undefined
            ? BOOLEAN_SIGNAL_THRESHOLD
            : alarmThresholds?.maximum,
      }
    : alarmThresholds;

  // Get the min and max from data AND alarm thresholds so threshold lines are always visible
  const dataMin = Math.min(
    ...normalizedDataPoints.map((d) => d.min),
    normalizedThresholds?.minimum ?? Infinity,
    normalizedThresholds?.maximum ?? Infinity,
  );
  const dataMax = Math.max(
    ...normalizedDataPoints.map((d) => d.max),
    normalizedThresholds?.minimum ?? -Infinity,
    normalizedThresholds?.maximum ?? -Infinity,
  );

  // Add small padding
  const padding = isBooleanSignal ? 0.1 : (dataMax - dataMin) * 0.1 || 5;
  let yMin = isBooleanSignal ? -0.1 : Math.floor(dataMin - padding);
  let yMax = isBooleanSignal ? 1.1 : Math.ceil(dataMax + padding);

  // Calculate nice step size
  const range = yMax - yMin;
  let step: number;
  if (isBooleanSignal) {
    step = 1;
  } else if (range <= 5) {
    step = 1;
  } else if (range <= 10) {
    step = 2;
  } else if (range <= 25) {
    step = 5;
  } else {
    step = Math.ceil(range / 5 / 10) * 10;
  }

  // Round yMin and yMax to step boundaries
  if (!isBooleanSignal) {
    yMin = Math.floor(yMin / step) * step;
    yMax = Math.ceil(yMax / step) * step;
  }

  // Create chart data with min and range, split by thresholds
  const chartData = normalizedDataPoints.map((point) => {
    const min = point.min;
    const max = point.max;
    const base = min - yMin; // Offset from yMin (not from 0)

    // Calculate segments based on thresholds
    const minThreshold = normalizedThresholds?.minimum;
    const maxThreshold = normalizedThresholds?.maximum;

    // Initialize segments
    let belowMin = 0;
    let inRange = 0;
    let aboveMax = 0;

    // Calculate segments based on where the bar falls relative to thresholds
    if (minThreshold !== undefined && maxThreshold !== undefined) {
      // Both thresholds defined
      if (max < minThreshold) {
        // Entire bar is below minimum
        belowMin = max - min;
      } else if (min > maxThreshold) {
        // Entire bar is above maximum
        aboveMax = max - min;
      } else {
        // Bar spans multiple regions
        if (min < minThreshold) {
          belowMin = Math.min(max, minThreshold) - min;
        }

        if (max > maxThreshold) {
          aboveMax = max - Math.max(min, maxThreshold);
        }

        const rangeStart = Math.max(min, minThreshold);
        const rangeEnd = Math.min(max, maxThreshold);
        if (rangeEnd > rangeStart) {
          inRange = rangeEnd - rangeStart;
        }
      }
    } else if (minThreshold !== undefined) {
      // Only minimum threshold
      if (max < minThreshold) {
        belowMin = max - min;
      } else if (min < minThreshold) {
        belowMin = minThreshold - min;
        inRange = max - minThreshold;
      } else {
        inRange = max - min;
      }
    } else if (maxThreshold !== undefined) {
      // Only maximum threshold
      if (min > maxThreshold) {
        aboveMax = max - min;
      } else if (max > maxThreshold) {
        inRange = maxThreshold - min;
        aboveMax = max - maxThreshold;
      } else {
        inRange = max - min;
      }
    } else {
      // No thresholds - all in range
      inRange = max - min;
    }

    return {
      time: point.time,
      base,
      belowMin,
      inRange,
      aboveMax,
    };
  });

  // Generate ticks based on the step
  const ticks = [];

  // Generate ticks from yMin to yMax in actual value space, then transform
  if (isBooleanSignal) {
    ticks.push(0 - yMin, 1 - yMin);
  } else {
    for (let actualValue = yMin; actualValue <= yMax; actualValue += step) {
      const transformedValue = actualValue - yMin;
      ticks.push(transformedValue);
    }
  }

  return (
    <div className='flex items-start gap-3.5 select-none'>
      {/* Y-axis Labels */}
      <div
        className='size-caption text-secondary relative ml-1 h-50.75 w-fit
          min-w-8 pt-1 pr-1 text-right'
      >
        {ticks
          .slice()
          .reverse()
          .map((tick, index) => {
            // Position each label based on its tick value
            const positionPercent =
              ((yMax - yMin - tick) / (yMax - yMin)) * 100;
            return (
              <span
                key={index}
                className='absolute right-0 whitespace-nowrap'
                style={{
                  top: `${positionPercent}%`,
                  transform: 'translateY(-50%)',
                }}
              >
                {isBooleanSignal
                  ? toBinarySignalState(tick + yMin) === 1
                    ? 'on'
                    : 'off'
                  : `${tick + yMin} ${unit}`}
              </span>
            );
          })}
      </div>

      {/* Chart */}
      <div className='flex-1 **:outline-none [&_svg]:select-none'>
        <ResponsiveContainer width='100%' height={203}>
          <BarChart
            data={chartData}
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            barGap={0}
            barCategoryGap='5%'
          >
            <CartesianGrid
              strokeDasharray='0'
              stroke='rgb(231 234 238)' // neutral-100
              vertical={false}
            />
            <XAxis hide dataKey='time' />
            <YAxis
              hide
              domain={[0, yMax - yMin]}
              type='number'
              allowDataOverflow={false}
              ticks={ticks}
            />

            {/* Threshold lines — not applicable for boolean (Machine Alarm) signals */}
            {!isBooleanSignal && normalizedThresholds?.minimum !== undefined && (
              <ReferenceLine
                y={normalizedThresholds.minimum - yMin}
                stroke='rgb(234 88 12)' // orange-600
                strokeDasharray='5 5'
                strokeWidth={2}
                opacity={0.75}
              />
            )}
            {!isBooleanSignal && normalizedThresholds?.maximum !== undefined && (
              <ReferenceLine
                y={normalizedThresholds.maximum - yMin}
                stroke='rgb(234 88 12)' // orange-600
                strokeDasharray='5 5'
                strokeWidth={2}
                opacity={0.75}
              />
            )}
            {/* Stacked bars: base (offset from yMin) + segments (below/in-range/above) */}
            <Bar
              dataKey='base'
              stackId='a'
              fill='transparent'
              isAnimationActive={false}
              maxBarSize={12}
            />
            {/* Below minimum threshold - red */}
            <Bar
              dataKey='belowMin'
              stackId='a'
              fill='rgb(239 68 68)' // red-500
              stroke='rgb(254 226 226)' // red-100
              strokeWidth={0.5}
              isAnimationActive={false}
              maxBarSize={12}
            />
            {/* In valid range - blue */}
            <Bar
              dataKey='inRange'
              stackId='a'
              fill='rgb(123 187 243)'
              stroke='rgb(222 239 254)'
              strokeWidth={0.5}
              isAnimationActive={false}
              maxBarSize={12}
            />
            {/* Above maximum threshold - red */}
            <Bar
              dataKey='aboveMax'
              stackId='a'
              fill='rgb(239 68 68)' // red-500
              stroke='rgb(254 226 226)' // red-100
              strokeWidth={0.5}
              isAnimationActive={false}
              maxBarSize={12}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
